import { useMutation, useQuery } from "@tanstack/react-query"
import CustomModelNumbersService from "services/customModelNumbers.service"
import type { TCustomModelNumberPayload } from "types/customModelNumbers.type"

/**
 * Exported so every writer of a custom model number invalidates the SAME key — the management
 * screen (#1345) and the quotation editor's Custom tab (#1346) both create definitions, and a
 * second hardcoded string is how the two lists silently drift apart.
 */
export const CUSTOM_MODEL_NUMBERS_QUERY_KEY = "customModelNumbers"

export const useGetCustomModelNumbers = (drawingId: number, enabled = true) => {
	return useQuery({
		queryKey: [CUSTOM_MODEL_NUMBERS_QUERY_KEY, drawingId],
		queryFn: () => CustomModelNumbersService.getCustomModelNumbers(drawingId),
		select: ({ data }) => data,
		enabled: enabled && Number.isFinite(drawingId) && drawingId > 0,
	})
}

export const useCreateCustomModelNumber = () => {
	return useMutation({
		mutationFn: ({ drawingId, payload }: { drawingId: number; payload: TCustomModelNumberPayload }) =>
			CustomModelNumbersService.createCustomModelNumber(drawingId, payload),
	})
}

export const useUpdateCustomModelNumber = () => {
	return useMutation({
		mutationFn: ({
			drawingId,
			customModelNumberId,
			payload,
		}: {
			drawingId: number
			customModelNumberId: number
			payload: TCustomModelNumberPayload
		}) => CustomModelNumbersService.updateCustomModelNumber(drawingId, customModelNumberId, payload),
	})
}

export const useDeleteCustomModelNumber = () => {
	return useMutation({
		mutationFn: ({ drawingId, customModelNumberId }: { drawingId: number; customModelNumberId: number }) =>
			CustomModelNumbersService.deleteCustomModelNumber(drawingId, customModelNumberId),
	})
}
